/**
 * Subscription-gift counters on the PDP.
 *
 * One fetch of the gift engine drives two lines in the banner:
 *
 *   headline  "Only [n] free gift bundles left"               [n] = units left
 *   sub-row   "[a] live shoppers added this in the past hour"  [a] = recent adds
 *
 * Both figures come from real data that can actually move. Nothing here
 * invents, animates towards, or caches a number.
 *
 * Three response states, three behaviours:
 *
 *   - Batch open. The headline takes the live count; the sub-row takes the
 *     activity count, or a fixed non-numeric line when activity is below the
 *     floor. The floor defaults to 1, so any real activity is reported and the
 *     fixed line only stands in when nothing has happened this hour.
 *   - Batch closed. This is the cooldown between releases, about a minute. The
 *     banner stays up and the headline counts down to reopensAt, then polls
 *     until the next batch actually opens. The countdown is driven from an
 *     absolute instant supplied by the server, not a duration, so it stays
 *     correct even when the response is served from the edge cache.
 *   - State unknown (failed fetch, 503, malformed body). The banner hides, and
 *     a retry is scheduled — a blip should cost the banner one cycle, not the
 *     whole session.
 *
 * Until the first response lands the banner renders as a skeleton, marked by
 * data-gift-pending. Every path below clears or hides it; the CSS carries a
 * timed backstop for the case where this file never runs at all.
 *
 * ── Staying live ────────────────────────────────────────────────────────────
 *
 * The counters re-poll while the shopper is on the page, so a batch claimed or
 * a cart added by someone else moves the number under them rather than waiting
 * for a reload. Changed figures animate; unchanged ones are left alone.
 *
 * The interval is matched to the endpoint, not to how live we would like to
 * look. /api/public/gift-bonus is edge-cached for 60s while a batch is open
 * (its claim count pages the Shopify Admin API, and the rate limit is the
 * scarce resource — see the handler's own comment). Polling faster than that
 * returns a byte-identical cached body, so it would buy no freshness and spend
 * requests to get it. 30s keeps the edge cache warm and picks up every real
 * change within about a minute.
 *
 * That is genuinely as live as the underlying data: `remaining` moves when a
 * paid subscription order lands and `activeCarts` is a rolling 60-minute
 * window, so at current volumes both change on the order of once every 10–30
 * minutes. A shopper who sees a tick is seeing a real event. Most sessions will
 * see none, and that is the honest outcome — the alternative is inventing
 * movement, which is the one thing this whole module is built not to do.
 *
 * Polling stops while the tab is hidden and resumes with an immediate fetch, so
 * a backgrounded tab costs nothing and a returning one is never stale.
 *
 * This file also beacons the add-to-cart that feeds [a]. That write is
 * deliberately NOT the A/B funnel beacon in ab-track.js: that ingest
 * deduplicates on [test, visitor, event] so its figures are
 * unique-visitors-ever rather than recent activity, and it stops accepting
 * writes once a test is stopped.
 */
(function () {
  // An array rather than the live NodeList: the drawer mirror below is created
  // at runtime and has to join this list.
  var blocks = Array.prototype.slice.call(document.querySelectorAll('[data-gift-endpoint]'));
  if (!blocks.length) return;

  var DEFAULT_MIN_ACTIVITY = 1;
  var FETCH_TIMEOUT_MS = 4000;
  /** How often to re-ask once the countdown has run out. The release cron runs
   *  once a minute, so the reopen can land up to a minute after reopensAt. */
  var REOPEN_POLL_MS = 5000;
  /** Give up after this long past reopensAt and hide, rather than poll forever
   *  against an engine that is evidently not releasing. */
  var REOPEN_POLL_LIMIT_MS = 180000;
  /** Steady-state re-poll. See the note on matching the endpoint's 60s cache. */
  var LIVE_POLL_MS = 30000;
  /** Spread concurrent viewers so a popular PDP does not synchronise its
   *  readers into one spike every 30 seconds. */
  var LIVE_JITTER_MS = 6000;
  /** After an unknown state. Slower than the live cadence: if the engine is
   *  down, backing off is worth more than a prompt recovery. */
  var RETRY_MS = 60000;
  /** Shortest gap between reads triggered by the tab regaining focus. */
  var VISIBILITY_MIN_GAP_MS = 10000;

  /** Blocks that render a source block's state without fetching for themselves.
   *  Currently just the copy inside the UpCart drawer. */
  var mirrors = [];

  /** Tokens backed by a figure. These drive pluralisation, the change tick, and
   *  whether the row is carrying a real number at all (which is what the dot
   *  vouches for). */
  var TOKENS = /\[a\]|\[c\]|\[n\]/;
  /** Everything build() splits on. [live] carries no data — it is the marker
   *  word, styled as a count so it reads as part of the figure beside it. */
  var TOKENS_G = /(\[a\]|\[c\]|\[n\]|\[live\])/g;

  /* ── read: the counters ──────────────────────────────────────────────── */

  /**
   * Splits the response into its independent claims.
   *
   * Returns null for "state unknown" — a failed fetch, a 503, a malformed body.
   * `batch` is null unless a batch is genuinely open with units left; that is
   * the only state in which a "N left" claim means anything.
   */
  function parse(raw) {
    if (!raw || raw.ok !== true) return null;

    var remaining = Number(raw.remaining);
    var claimed = Number(raw.claimed);
    var cap = Number(raw.cap);
    var active = Number(raw.activeCarts);

    var batch = null;
    if (raw.open === true && Number.isFinite(remaining) && remaining >= 1) {
      batch = {
        remaining: remaining,
        claimed: Number.isFinite(claimed) && claimed > 0 ? claimed : null,
        cap: Number.isFinite(cap) ? cap : null,
      };
    }

    var reopensAt = typeof raw.reopensAt === 'string' ? Date.parse(raw.reopensAt) : NaN;

    return {
      batch: batch,
      activeCarts: Number.isFinite(active) && active >= 0 ? active : null,
      reopensAt: Number.isFinite(reopensAt) ? reopensAt : null,
      item: '',
    };
  }

  /** Whether every placeholder this template needs has a value behind it. */
  function satisfied(tpl, state, minActivity) {
    if (tpl.indexOf('[a]') > -1 && !(state.activeCarts !== null && state.activeCarts >= minActivity)) {
      return false;
    }
    if (tpl.indexOf('[c]') > -1 && !(state.batch && state.batch.claimed !== null)) return false;
    if ((tpl.indexOf('[n]') > -1 || tpl.indexOf('[cap]') > -1) && !state.batch) return false;
    return true;
  }

  function valueFor(token, state) {
    if (token === '[a]') return state.activeCarts;
    if (token === '[c]') return state.batch.claimed;
    return state.batch.remaining;
  }

  // [cap] = batch size, [item] = gift name, [s] = plural suffix. Square brackets
  // rather than braces: these templates are emitted inside a Liquid {{ }} output
  // tag, and a brace in there breaks Liquid's tokenizer.
  function fill(str, state, primary) {
    var cap = state.batch && state.batch.cap !== null ? String(state.batch.cap) : '';
    return str
      .replace(/\[cap\]/g, cap)
      .replace(/\[item\]/g, state.item)
      .replace(/\[s\]/g, primary === 1 ? '' : 's');
  }

  /**
   * Builds the line into `target`, giving each count its own styled element.
   *
   * Returns early when the line would render byte-identically to what is
   * already there. Rebuilding regardless would restart the CSS animation on
   * every poll — turning a tick that should mean "this just changed" into a
   * pulse every 30 seconds — and would re-announce the unchanged line to
   * screen readers through the row's aria-live.
   *
   * A count whose value moved since the last render is marked so the
   * stylesheet can draw attention to it, with the direction it moved. `prev` is
   * a snapshot taken before this render pass, shared by the headline and the
   * row, so both compare against the same baseline.
   *
   * Author-supplied copy never reaches innerHTML — the theme editor is trusted,
   * but a scarcity line is the last place worth leaving an injection path open.
   */
  /** Drop change flags, leaving the counts themselves untouched. */
  function clearTicks(target) {
    var flagged = target.querySelectorAll('.sub-gifts__bonus-count--tick');
    for (var i = 0; i < flagged.length; i++) {
      flagged[i].className = 'sub-gifts__bonus-count';
    }
  }

  function build(target, tpl, state, prev) {
    // [s] agrees with whichever count the line actually shows, so "[a]
    // shopper[s]" pluralises off the activity figure rather than the batch.
    var match = tpl.match(TOKENS);
    var primary = match ? valueFor(match[0], state) : null;
    var parts = tpl.split(TOKENS_G);

    var signature = '';
    parts.forEach(function (part) {
      if (part === '[a]' || part === '[c]' || part === '[n]') {
        signature += ' ' + String(valueFor(part, state));
      } else if (part === '[live]') {
        signature += ' ' + state.live;
      } else if (part) {
        signature += fill(part, state, primary);
      }
    });
    if (target.__giftSig === signature) {
      // Line is unchanged, so it keeps the nodes it has — but those may still
      // be flagged from an earlier change, and the two lines do not move
      // together: the headline can tick while the row holds. Left alone the
      // flag would outlive the change it describes. Nothing re-animates here;
      // the animation is spent by now and only restarts on re-insertion.
      clearTicks(target);
      return;
    }
    target.__giftSig = signature;

    target.textContent = '';
    parts.forEach(function (part) {
      if (part === '[a]' || part === '[c]' || part === '[n]') {
        var value = valueFor(part, state);
        var count = document.createElement('span');
        count.className = 'sub-gifts__bonus-count';

        // Freshly created, so the animation runs on insertion and needs no
        // teardown: the next change replaces the node outright.
        var was = prev ? prev[part] : undefined;
        if (typeof was === 'number' && was !== value) {
          count.className += ' sub-gifts__bonus-count--tick';
          count.className += value > was
            ? ' sub-gifts__bonus-count--up'
            : ' sub-gifts__bonus-count--down';
        }

        count.textContent = String(value);
        target.appendChild(count);
      } else if (part === '[live]') {
        // Same class as a count, so "6 live" reads as one styled figure rather
        // than a number with a word after it. Never ticks: it does not change,
        // and flagging it would animate a constant.
        if (state.live) {
          var word = document.createElement('span');
          word.className = 'sub-gifts__bonus-count';
          word.textContent = state.live;
          target.appendChild(word);
        }
      } else if (part) {
        target.appendChild(document.createTextNode(fill(part, state, primary)));
      }
    });
  }

  /** Current values keyed by token, for whichever tokens have data behind them. */
  function values(state) {
    var out = {};
    if (state.activeCarts !== null) out['[a]'] = state.activeCarts;
    if (state.batch) {
      out['[n]'] = state.batch.remaining;
      if (state.batch.claimed !== null) out['[c]'] = state.batch.claimed;
    }
    return out;
  }

  /** m:ss, or h:mm:ss once there is an hour to show. Never negative. */
  function clock(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var s = total % 60;
    var m = Math.floor(total / 60) % 60;
    var h = Math.floor(total / 3600);
    var pad = function (n) {
      return n < 10 ? '0' + n : String(n);
    };
    return h > 0 ? h + ':' + pad(m) + ':' + pad(s) : m + ':' + pad(s);
  }

  function minActivity(block) {
    var min = Number(block.getAttribute('data-gift-min'));
    return Number.isFinite(min) && min >= 1 ? min : DEFAULT_MIN_ACTIVITY;
  }

  /**
   * The sub-row: the activity line when it holds up, the fixed line otherwise.
   *
   * The live marker rides on this row and is shown only when the line actually
   * carries a live figure. Beside the fixed fallback ("Free with every
   * subscription") a pulsing dot would be claiming liveness for a static
   * sentence, which is precisely the kind of decoration this banner avoids.
   */
  function renderRow(block, state, prev) {
    var row = block.querySelector('[data-gift-bonus]');
    if (!row) return;
    var target = row.querySelector('[data-gift-bonus-text]');
    if (!target) return;

    var marker = row.querySelector('[data-gift-live]');
    var min = minActivity(block);
    var tpl = row.getAttribute('data-text');
    if (!tpl || !satisfied(tpl, state, min)) tpl = row.getAttribute('data-fallback') || '';
    if (!tpl || !satisfied(tpl, state, min)) {
      row.hidden = true;
      if (marker) marker.hidden = true;
      return;
    }

    build(target, tpl, state, prev);
    if (marker) marker.hidden = !TOKENS.test(tpl);
    row.hidden = false;
  }

  function reveal(block) {
    block.hidden = false;
    block.removeAttribute('data-gift-pending');
  }

  function clearTimers(block) {
    if (block.__giftTick) {
      clearInterval(block.__giftTick);
      block.__giftTick = null;
    }
    if (block.__giftPoll) {
      clearTimeout(block.__giftPoll);
      block.__giftPoll = null;
    }
  }

  /**
   * Queue the next read.
   *
   * A hidden tab is left alone entirely — its numbers are not on screen, and
   * the visibility handler fetches immediately on return, so nothing is gained
   * by keeping the cycle running behind a backgrounded page.
   */
  function schedule(block, delay) {
    // A mirror renders whatever its source just fetched, so giving it its own
    // cycle would double the request volume to show the same numbers twice.
    if (block.__giftMirror) return;
    if (block.__giftPoll) clearTimeout(block.__giftPoll);
    block.__giftPoll = setTimeout(function () {
      block.__giftPoll = null;
      if (document.hidden) {
        schedule(block, delay);
        return;
      }
      load(block);
    }, delay + Math.floor(Math.random() * LIVE_JITTER_MS));
  }

  /** Batch open: headline takes the count, row takes activity. */
  function renderOpen(block, state, prev) {
    var headline = block.querySelector('[data-gift-headline]');
    var tpl = headline && headline.getAttribute('data-text');
    if (tpl && satisfied(tpl, state, minActivity(block))) build(headline, tpl, state, prev);
    renderRow(block, state, prev);
    reveal(block);
    schedule(block, LIVE_POLL_MS);
  }

  /**
   * Cooldown: headline counts down to the next release.
   *
   * Ticks off the absolute instant rather than a decrementing counter, so a tab
   * that was backgrounded (and had its timers throttled) shows the right number
   * on return instead of a drifted one.
   */
  function renderCooldown(block, state, prev) {
    var headline = block.querySelector('[data-gift-headline]');
    var tpl = block.getAttribute('data-gift-cooldown');
    if (!headline || !tpl || state.reopensAt === null) {
      block.hidden = true;
      schedule(block, RETRY_MS);
      return;
    }

    var deadline = state.reopensAt;

    var paint = function () {
      var left = deadline - Date.now();
      headline.textContent = '';
      tpl.split(/(\[t\])/g).forEach(function (part) {
        if (part === '[t]') {
          var el = document.createElement('span');
          el.className = 'sub-gifts__bonus-count';
          el.textContent = clock(left);
          headline.appendChild(el);
        } else if (part) {
          headline.appendChild(document.createTextNode(part));
        }
      });

      if (left <= 0) {
        clearTimers(block);
        // The cron releases on its own schedule, so the reopen lands some time
        // after the deadline. Ask again rather than assume.
        if (Date.now() - deadline > REOPEN_POLL_LIMIT_MS) {
          block.hidden = true;
          return;
        }
        block.__giftPoll = setTimeout(function () {
          load(block);
        }, REOPEN_POLL_MS);
      }
    };

    // The countdown owns the headline while it runs, so the shared signature
    // cache must not carry a stale entry into the next open render.
    headline.__giftSig = null;
    renderRow(block, state, prev);
    reveal(block);
    paint();
    if (deadline - Date.now() > 0) block.__giftTick = setInterval(paint, 1000);
  }

  function apply(block, state) {
    clearTimers(block);
    if (!state) {
      block.hidden = true;
      schedule(block, RETRY_MS);
      return;
    }
    state.item = block.getAttribute('data-gift-item') || '';
    state.live = block.getAttribute('data-gift-live-word') || '';

    // Taken before either line renders so the headline and the row compare
    // against the same baseline, and stored after both, so a value that only
    // becomes displayable on this pass is not reported as having moved.
    var prev = block.__giftValues;
    if (state.batch) renderOpen(block, state, prev);
    else renderCooldown(block, state, prev);
    block.__giftValues = values(state);
  }

  function withProduct(endpoint, productId) {
    if (!productId) return endpoint;
    return endpoint + (endpoint.indexOf('?') > -1 ? '&' : '?') + 'product=' + encodeURIComponent(productId);
  }

  /**
   * Render one response into its block and into anything mirroring it.
   *
   * The state object is handed to each in turn rather than copied: apply()
   * re-reads item and live word from the block it is rendering, and the calls
   * are sequential, so there is nothing to interleave. Keeping the last
   * response on the block lets a mirror mounted between polls paint straight
   * away instead of showing an empty band until the next fetch.
   */
  function applyAll(source, state) {
    source.__giftLastState = state;
    apply(source, state);
    for (var i = mirrors.length - 1; i >= 0; i--) {
      // A drawer re-render detaches the old mirror; drop it rather than
      // rendering into a node nobody can see.
      if (!mirrors[i].isConnected) {
        mirrors.splice(i, 1);
        continue;
      }
      if (mirrors[i].__giftSource === source) apply(mirrors[i], state);
    }
  }

  function load(block) {
    var endpoint = block.getAttribute('data-gift-endpoint');
    if (!endpoint) return;
    block.__giftLoadedAt = Date.now();

    // Times out rather than hanging: an unconfirmed counter resolves to the
    // hidden state, which is the correct degraded result.
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, FETCH_TIMEOUT_MS)
      : null;

    fetch(withProduct(endpoint, block.getAttribute('data-gift-product')), {
      method: 'GET',
      credentials: 'omit',
      signal: controller ? controller.signal : undefined,
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (raw) {
        applyAll(block, parse(raw));
      })
      .catch(function () {
        applyAll(block, null);
      })
      .then(function () {
        if (timer) clearTimeout(timer);
      });
  }

  blocks.forEach(load);

  // Returning to the tab reads immediately rather than waiting out whatever was
  // left of the interval, so the numbers on screen are never older than the
  // moment the shopper looked back at them.
  //
  // Floored at VISIBILITY_MIN_GAP_MS because tab focus is cheap to trigger and
  // this handler is not: someone cycling between tabs, or a phone waking and
  // sleeping, would otherwise fire a read on every switch. Below the floor the
  // existing schedule is simply left to run.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    var now = Date.now();
    for (var i = 0; i < blocks.length; i++) {
      var block = blocks[i];
      if (now - (block.__giftLoadedAt || 0) < VISIBILITY_MIN_GAP_MS) continue;
      if (block.__giftPoll) {
        clearTimeout(block.__giftPoll);
        block.__giftPoll = null;
      }
      load(block);
    }
  });

  /* ── the same counters inside the UpCart drawer ──────────────────────── */

  /**
   * UpCart renders the whole drawer into an OPEN shadow root on #upCart, which
   * forces two things about how this works:
   *
   *   - The theme stylesheet does not cross a shadow boundary, so none of the
   *     PDP banner's styling reaches this markup. Verified, not assumed: a
   *     .sub-gifts__bonus-count placed in that root computes to plain black
   *     rather than the brand red the theme gives it. The styling therefore
   *     arrives as its own <link> to gift-bonus-drawer.css, loaded into the root.
   *   - Nothing inside is reachable from document.querySelector, so Liquid
   *     cannot render this — it has to be built here and injected.
   *
   * Placement stays the merchant's. UpCart's "below cart items" HTML field
   * holds an empty <div data-gift-cart-slot></div> and this fills it. Anchoring
   * to UpCart's own class names instead would work today and break on the app's
   * next DOM change; a slot the merchant owns will not move under us.
   *
   * The mirror never fetches. It paints whatever its source block last
   * received, so the drawer and the page always agree and always tick together,
   * on one request rather than two.
   */

  var CART_HOST_ID = 'upCart';
  var CART_SLOT = '[data-gift-cart-slot]';
  /** UpCart is an app embed and boots on its own schedule; bounded so a store
   *  without it does not leave a timer running for the life of the page. */
  var CART_WAIT_TRIES = 40;
  var CART_WAIT_MS = 500;

  var cartObserver = null;
  var cartTries = 0;
  var mountQueued = false;

  function cartRoot() {
    var host = document.getElementById(CART_HOST_ID);
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function ensureCartStyles(root, href) {
    if (!href || root.querySelector('link[data-gift-cart-css]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-gift-cart-css', '');
    root.insertBefore(link, root.firstChild);
  }

  /** Copies the source block's configuration and template strings, so the two
   *  banners cannot drift: there is one set of theme settings behind both. */
  function buildMirror(source) {
    var el = document.createElement('div');
    el.className = 'sub-gifts-cart';
    el.hidden = true;
    el.setAttribute('data-gift-pending', '');

    var carried = [
      'data-gift-endpoint',
      'data-gift-product',
      'data-gift-min',
      'data-gift-cooldown',
      'data-gift-item',
      'data-gift-live-word',
    ];
    for (var i = 0; i < carried.length; i++) {
      var v = source.getAttribute(carried[i]);
      if (v !== null) el.setAttribute(carried[i], v);
    }

    var headline = document.createElement('p');
    headline.className = 'sub-gifts-cart__headline';
    headline.setAttribute('data-gift-headline', '');
    var sourceHead = source.querySelector('[data-gift-headline]');
    headline.setAttribute('data-text', (sourceHead && sourceHead.getAttribute('data-text')) || '');
    el.appendChild(headline);

    var sourceRow = source.querySelector('[data-gift-bonus]');
    if (sourceRow) {
      var row = document.createElement('p');
      row.className = 'sub-gifts-cart__bonus';
      row.setAttribute('data-gift-bonus', '');
      row.setAttribute('data-text', sourceRow.getAttribute('data-text') || '');
      row.setAttribute('data-fallback', sourceRow.getAttribute('data-fallback') || '');
      row.setAttribute('aria-live', 'polite');
      row.hidden = true;

      var marker = document.createElement('span');
      marker.className = 'sub-gifts__bonus-live';
      marker.setAttribute('data-gift-live', '');
      marker.hidden = true;
      var dot = document.createElement('span');
      dot.className = 'sub-gifts__bonus-dot';
      marker.appendChild(dot);
      row.appendChild(marker);

      var text = document.createElement('span');
      text.setAttribute('data-gift-bonus-text', '');
      row.appendChild(text);

      el.appendChild(row);
    }

    el.__giftMirror = true;
    el.__giftSource = source;
    return el;
  }

  /**
   * A/B pdp_quiz_unlock_2608 — the drawer's band, for a shopper who has not
   * taken the quiz yet.
   *
   * The bundle counter is scarcity for something they are already getting. At
   * the moment the cart opens, the more useful thing to say to that shopper is
   * that the price in front of them is not the best one available, and what it
   * takes to change it. So in that one state the band carries the offer
   * instead of the count.
   *
   * Arm B only. Promoting the quiz to the control would be a second change
   * inside the experiment, and the arm would stop being the page as it stands.
   */
  /* What the quiz is worth to the cart she actually has.

     data-quiz-unlock-max is the largest figure on the page — the six-month
     ladder's 36% — because it is the maximum across every variant card. Said
     over a one-time single bottle it promises a discount that bottle cannot
     earn: hers is 22%. So the plan in the cart chooses the number, and the
     page-wide maximum is only the fallback for a cart we cannot read.

     The map comes from the quiz's own config, which is rendered on this page
     for the slide-up, so the two surfaces cannot disagree about what a plan is
     worth. */
  var quizOffer = null;

  function quizConfig() {
    try {
      var el = document.querySelector('[data-hq-config]');
      if (!el) return null;
      var cfg = JSON.parse(el.textContent);
      var d = cfg.discount || {};
      if (!d.totals || !cfg.plans) return null;
      return { totals: d.totals, targets: d.targets || {}, plans: cfg.plans };
    } catch (e) { return null; }
  }

  function readQuizOffer(done) {
    var cfg = quizConfig();
    if (!cfg) { done(null); return; }
    try {
      window.fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) {
          /* The longest plan in the cart, since that is the one whose ladder
             she is closest to and the largest figure she can honestly be
             offered. */
          var months = null;
          var items = (c && c.items) || [];
          for (var i = 0; i < items.length; i++) {
            for (var j = 0; j < cfg.plans.length; j++) {
              if (String(cfg.plans[j].variant_id) !== String(items[i].variant_id)) continue;
              var m = Number(cfg.plans[j].months);
              if (m > 0 && (months === null || m > months)) months = m;
            }
          }
          /* Whether there is a plan for the discount to come off at all.

             The quiz discounts subscriptions. Over a cart holding only a
             one-time bottle, "unlock up to 22% off your plan" names something
             she has not got and cannot earn without changing what she is
             buying — so the band does not belong there, and the gift counter
             it displaced is the more honest thing to show. */
          var hasPlan = false;
          for (var k = 0; k < items.length; k++) {
            if (items[k].selling_plan_allocation) { hasPlan = true; break; }
          }
          if (months === null) { done({ pct: 0, target: null, hasPlan: hasPlan }); return; }
          var total = Number(cfg.totals[String(months)]);
          var target = Number(cfg.targets[String(months)]);
          done({
            pct: total > 0 ? total : 0,
            target: target > 0 ? target : null,
            hasPlan: hasPlan
          });
        })['catch'](function () { done(null); });
    } catch (e) { done(null); }
  }

  /* Put the band back to whatever the cart should be showing instead. */
  function remountCart() {
    var root = cartRoot();
    var slot = root && root.querySelector(CART_SLOT);
    var band = slot && slot.querySelector('.sub-gifts-cart');
    if (band && band.parentNode) band.parentNode.removeChild(band);
    mountCart();
  }

  function quizEligible() {
    var el = document.documentElement;
    if (el.getAttribute('data-ab3') !== 'b') return false;
    if (el.getAttribute('data-quiz-unlock') === 'yes') return false;
    return true;
  }

  /* Ask the cart again, every time the band is mounted.

     The answer was cached and then trusted, which is wrong for a fact about a
     cart that changes: read once while the cart was empty — which is exactly
     when the drawer is first built, before the add that opens it has landed —
     and "no plan here" stuck, so a subscription added a moment later got the
     gift counter instead of its own offer. The cache is now only what to paint
     before the answer arrives, never what to decide on. */
  function verifyQuizBand() {
    readQuizOffer(function (offer) {
      if (!offer) return;
      quizOffer = offer;
      var root = cartRoot();
      var slot = root && root.querySelector(CART_SLOT);
      if (!slot) return;
      var band = slot.querySelector('.sub-gifts-cart--quiz');
      var want = quizEligible() && offer.hasPlan;
      /* Showing the wrong one of the two: swap them. */
      if (want !== !!band) { remountCart(); return; }
      if (!band) return;
      var head = band.querySelector('.sub-gifts-cart__headline');
      if (head && offer.pct > 0) {
        head.textContent = 'Unlock up to ' + offer.pct + '% off your plan';
      }
      /* From the stored base, so a second pass cannot append a second query. */
      var base = band.getAttribute('data-quiz-href') || band.getAttribute('href');
      if (base && offer.target) band.setAttribute('href', base + '?target=' + offer.target);
    });
  }

  function quizPromo(source) {
    if (!quizEligible()) return null;
    /* Nothing in the cart the quiz can discount — as far as the last read
       knew. verifyQuizBand corrects it either way once the cart answers. */
    if (quizOffer && !quizOffer.hasPlan) return null;

    var max = source.getAttribute('data-quiz-unlock-max');
    var href = source.getAttribute('data-quiz-unlock-href');
    if (!max || !href) return null;

    var wrap = document.createElement('a');
    wrap.className = 'sub-gifts-cart sub-gifts-cart--quiz';
    /* Opens the slide-up rather than the page it points at. The href stays a
       real one, so it still works without JS and still opens in a new tab if
       she asks for that. */
    wrap.setAttribute('data-quiz-open', '');
    wrap.setAttribute('data-quiz-href', href);
    wrap.setAttribute('href', href);

    var head = document.createElement('p');
    head.className = 'sub-gifts-cart__headline';
    var pct = quizOffer && quizOffer.pct > 0 ? quizOffer.pct : max;
    head.textContent = 'Unlock up to ' + pct + '% off your plan';
    wrap.appendChild(head);

    var sub = document.createElement('p');
    sub.className = 'sub-gifts-cart__bonus';
    sub.textContent = 'Take the two-minute quiz \u2192';
    wrap.appendChild(sub);

    return wrap;
  }

  function mountCart() {
    var root = cartRoot();
    if (!root) return;
    var slot = root.querySelector(CART_SLOT);
    if (!slot || slot.querySelector('.sub-gifts-cart')) return;

    var source = blocks[0];
    ensureCartStyles(root, source.getAttribute('data-gift-cart-css'));

    /* Mounted in place of the counter, not beside it — two bands in a column
       that already carries an upsell row and a checkout button is one band too
       many, and the counter is the one with less to say here. */
    var promo = quizPromo(source);
    if (promo) {
      slot.appendChild(promo);
      verifyQuizBand();
      return;
    }
    /* Mounted the counter — but if the cart turns out to hold a plan, the
       offer belongs here instead. */
    if (quizEligible()) verifyQuizBand();

    var mirror = buildMirror(source);
    slot.appendChild(mirror);
    mirrors.push(mirror);

    // Paint from the last response rather than waiting for the next poll: the
    // drawer opens on add-to-cart, which is exactly when a blank band would be
    // most conspicuous. undefined means nothing has come back yet, which is not
    // the same as a response that said "unknown" — leave it pending for that.
    if (source.__giftLastState !== undefined) apply(mirror, source.__giftLastState);
  }

  function queueMount() {
    if (mountQueued) return;
    mountQueued = true;
    setTimeout(function () {
      mountQueued = false;
      mountCart();
    }, 0);
  }

  /**
   * Watches the shadow root rather than trusting UpCart's lifecycle callbacks
   * alone. Those fire around a re-render, not necessarily after the DOM has
   * settled, and a re-render replaces the HTML field's contents — taking the
   * mirror with it. An observer re-mounts on whatever actually happened.
   */
  function watchCart() {
    var root = cartRoot();
    if (!root) {
      if (cartTries++ < CART_WAIT_TRIES) setTimeout(watchCart, CART_WAIT_MS);
      return;
    }
    mountCart();
    if (cartObserver || typeof MutationObserver !== 'function') return;
    cartObserver = new MutationObserver(queueMount);
    cartObserver.observe(root, { childList: true, subtree: true });
  }

  watchCart();

  /* ── write: the add-to-cart beacon behind [a] ────────────────────────── */

  var beaconBlock = null;
  for (var i = 0; i < blocks.length; i++) {
    if (blocks[i].getAttribute('data-gift-beacon') && blocks[i].getAttribute('data-gift-product')) {
      beaconBlock = blocks[i];
      break;
    }
  }
  if (!beaconBlock) return;

  var BEACON = beaconBlock.getAttribute('data-gift-beacon');
  var PRODUCT_ID = beaconBlock.getAttribute('data-gift-product');

  // The anonymous, random _ab_vid cookie set by snippets/ab-assign.liquid. It is
  // read rather than re-minted so a returning shopper is one visitor, and it is
  // not tied to an A/B arm — the cookie outlives any single test. No PII.
  function visitorId() {
    var m = document.cookie.match('(^|;)\\s*_ab_vid\\s*=\\s*([^;]+)');
    if (m) return m.pop();
    return window.__ab && window.__ab.visitorId ? window.__ab.visitorId : null;
  }

  var sent = false;

  function beacon() {
    // Once per pageview. The count is distinct visitors, so a shopper adding
    // three times is one row server-side anyway; this just avoids the writes.
    if (sent) return;
    var vid = visitorId();
    if (!vid) return;
    sent = true;

    var payload = JSON.stringify({ visitorId: vid, productId: PRODUCT_ID });

    // text/plain keeps this a CORS simple request — no preflight to fail while
    // the page unloads toward the cart drawer or checkout.
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(BEACON, new Blob([payload], { type: 'text/plain' }));
        return;
      }
    } catch (e) {}

    try {
      fetch(BEACON, {
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        credentials: 'omit',
      }).catch(function () {});
    } catch (e) {}
  }

  // Patching fetch catches every add-to-cart path the theme uses — the product
  // form, the "buy once" link, and the subscription handler in global.js —
  // without hooking each one. Composes with the other fetch wrappers on the
  // page (ab-track.js, cart-drawer.js): each delegates to the previous.
  var nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isAdd = /\/cart\/add(\.js)?/.test(url);

    return nativeFetch.apply(this, arguments).then(function (res) {
      // Deliberately does NOT force a re-read. The shopper's own add is part of
      // [a], but the counter is edge-cached for 60s, so an immediate refetch
      // would return the same cached body and show nothing — and busting the
      // cache to beat it would put an Admin API call behind every add-to-cart,
      // which is the exact cost that cache exists to prevent. The next poll
      // picks it up like any other change.
      if (isAdd && res && res.ok) beacon();
      return res;
    });
  };
})();
